/* eslint-disable @typescript-eslint/no-explicit-any */
import { previewVersion } from "@/configuration/previewVersion";
import { useCurrentPath } from "@/hooks";
import { useDataStore, useFilterStore } from "@/stores";
import { LogLevels } from "@/utils/Utils";
import EditorJS, { OutputData } from "@editorjs/editorjs";
import Embed from "@editorjs/embed";
import Image from "@editorjs/image";
import List from "@editorjs/list";
import Marker from "@editorjs/marker";
import Underline from "@editorjs/underline";
import { useEffect, useRef, useState } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { BlockViewerProps } from "../../Models";

const BlockContentViewer = ({ data, elements, section }: BlockViewerProps) => {
    const versions = useDataStore((state) => state.versions);
    const activeVersionId = useFilterStore((state) => state.activeVersion);
    const activeVersion = versions.find((x) => x.id === activeVersionId);

    const [content, setContent] = useState<OutputData>();
    const editorRef = useRef<EditorJS>();

    const navigate = useNavigate();
    const currentPath = useCurrentPath();
    const params = useParams();
    const { locale } = params;
    const elementSlug = params["*"]?.split("/")[1];

    useEffect(() => {
        if (data && data.blocks) {
            for (let i = 0; i < data.blocks.length; i++) {
                const block = data.blocks[i];
                if (block.type === "image") continue;

                switch (block.type) {
                    case "paragraph":
                        block.data.text = parseLinks(block.data.text);
                        break;

                    case "list":
                        for (let j = 0; j < block.data.items.length; j++) {
                            block.data.items[j] = parseLinks(block.data.items[j]);
                        }

                        break;

                    default:
                        continue;
                }
            }
        }

        setContent(data);

        return () => {
            const editor = editorRef.current;
            if (editor) {
                if (!editor) return;
                editor.isReady.then(() => editor.destroy());
            }
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (content) initEditor();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content]);

    const parseLinks = (data: string | any): string | any => {
        const template = document.createElement("template");
        let content: string = "";
        let isObject = false;

        if (data instanceof Object) {
            isObject = true;
            content = data.content;
        } else {
            content = data as string;
        }

        template.innerHTML = content;

        const allLinks = template.content.querySelectorAll("[href]");
        for (let j = 0; j < allLinks.length; j++) {
            const link = allLinks[j] as HTMLLinkElement;
            const elementId = link.dataset.internalElementId;
            const targetElementSlug = link.dataset.internalElementSlug;

            if (elementId || targetElementSlug) {
                const element = elements.find((x) => x.elementId === elementId || x.slug === targetElementSlug);
                if (element) {
                    let target = generatePath(currentPath, { ...params, locale })
                        .replace(":/", "")
                        .replace(elementSlug!, element.slug);

                    if (!activeVersion!.isDefault && activeVersion?.name !== previewVersion.name)
                        target = `${target}?version=${activeVersion!.name}`;

                    link.href = target;
                } else {
                    link.replaceWith(link.childNodes[0]);
                }
            } else {
                link.target = "_blank";
            }
        }

        return isObject ? { ...data, content: template.innerHTML } : template.innerHTML;
    };

    const fixInternalLinks = () => {
        const allInternalLinks = document.querySelectorAll("[data-internal-element-id], [data-internal-element-slug]");
        for (let i = 0; i < allInternalLinks.length; i++) {
            const linkElement = allInternalLinks[i] as HTMLLinkElement;
            linkElement.onclick = (e) => {
                e.preventDefault();
                const elementId = linkElement.dataset.internalElementId ?? "";
                const targetElementSlug = linkElement.dataset.internalElementSlug ?? "";
                const element = elements.find((x) => x.elementId === elementId || x.slug === targetElementSlug)!;

                let target = generatePath(currentPath, { ...params, locale })
                    .replace(":/", "")
                    .replace(elementSlug!, element.slug);

                if (!activeVersion!.isDefault && activeVersion?.name !== previewVersion.name)
                    target = `${target}?version=${activeVersion!.name}`;

                navigate(target);
            };
        }
    };

    const initEditor = () => {
        const editor: EditorJS = new EditorJS({
            autofocus: false,
            logLevel: LogLevels.ERROR,
            onReady: () => fixInternalLinks(),
            holder: section.sectionCode,
            data: content,
            readOnly: true,
            tools: {
                image: {
                    class: Image,
                    inlineToolbar: true,
                    config: {
                        types: "image/jpeg, image/jpg, image/png, image/gif, video/mp4, video/quicktime",
                        field: "media",
                        uploader: {
                            uploadByFile: (blob: any) =>
                                new Promise((f) => {
                                    const reader = new FileReader();
                                    reader.onload = () =>
                                        f({
                                            success: 1,
                                            file: { url: reader.result },
                                        });
                                    reader.onerror = () => f({ success: 0 });
                                    reader.readAsDataURL(blob);
                                }),
                            uploadByUrl: (bt: any) => new Promise((f) => f({ success: 1, file: { url: bt } })),
                        },
                        features: {
                            caption: false,
                        },
                    },
                },
                underline: Underline,
                list: { class: List, inlineToolbar: true },
                embed: Embed,
                marker: { class: Marker },
            },
        });

        editorRef.current = editor;
    };

    return <div id={section.sectionCode} className={"editor-viewer relative"}></div>;
};

export default BlockContentViewer;
